import { useTranslation } from 'react-i18next';

import { NavLink } from './nav-link';
import { localePath } from '../lib/format';
import { useLanguageHref } from '../lib/use-language-href';
import { SITE_UI } from '@site-ui';
import type { FooterLink, SisterSite, SiteUi } from '../lib/site-ui';
import { SUPPORTED_LOCALES } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';

import css from './site-footer.module.css';

// The site's link lists (SITE_UI.footer): four entries each on desktop, the
// first three on phones (the Mobile board), some with a shorter phone label.
// On dubai a marble photo with a palm leaf on the left is the background, and
// the content starts right of the leaf on desktop (--footer-inset); on miami
// Biscayne Bay under a white veil (--footer-veil).
export function SiteFooter() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const languageHref = useLanguageHref();
  const sisters: readonly SisterSite[] = SITE_UI.sisterSites;
  // Widened to the SiteUi shape: footerImage is absent from miami's literal type.
  const { footerImage } = SITE_UI as SiteUi;

  const list = (items: readonly FooterLink[]) =>
    items.map((item) => (
      <NavLink key={item.path} href={localePath(locale, item.path)} className={css.link}>
        {item.short ? (
          <>
            <span className={css.long}>{t(item.key)}</span>
            <span className={css.short}>{t(item.short)}</span>
          </>
        ) : (
          t(item.key)
        )}
      </NavLink>
    ));

  // Bracketed by data-lastmod-skip sentinels, as riviera's footer: it is the
  // same on every page, so a change to it must not restamp the sitemap lastmod
  // of every URL at once — see packages/web-build/src/generate-sitemap.js.
  return (
    <>
      <div data-lastmod-skip="start" hidden />
      <footer className={css.footer}>
        {footerImage ? (
          <img className={css.photo} src={footerImage} alt="" loading="lazy" decoding="async" />
        ) : null}
        <div className={css.columns}>
          <div className={css.brand}>
            <NavLink href={localePath(locale)} aria-label={t('a11y.home')} className={css.home}>
              <SITE_UI.BrandLockup large />
            </NavLink>
            <p className={css.about}>{t('footer.about')}</p>
          </div>

          <nav aria-label={t('footer.thingsToDo')} className={css.column}>
            <p className={css.heading}>{t('footer.thingsToDo')}</p>
            {list(SITE_UI.footer.thingsToDo)}
          </nav>

          <nav aria-label={t('footer.areas')} className={css.column}>
            <p className={css.heading}>{t('footer.areas')}</p>
            {list(SITE_UI.footer.areas)}
          </nav>

          <div className={`${css.column} ${css.language}`}>
            <p className={`${css.heading} ${css.languageHeading}`}>{t('footer.language')}</p>
            <div className={css.languages}>
              {SUPPORTED_LOCALES.map((l) =>
                l === locale ? (
                  <span key={l} className={css.currentLanguage} aria-current="true">
                    {l.toUpperCase()}
                  </span>
                ) : (
                  <a key={l} href={languageHref(l)} hrefLang={l} lang={l}>
                    {l.toUpperCase()}
                  </a>
                ),
              )}
            </div>
          </div>
        </div>
        <p className={css.legal}>
          {t('footer.disclosure')}
          <span className={css.sister}>
            {t('footer.sisterSites')}
            {sisters.map((sister, i) => (
              <span key={sister.url}>
                {i > 0 ? ' · ' : ' '}
                <a href={`${sister.url}/${sister.locales.includes(locale) ? locale : 'en'}`}>
                  {sister.name}
                </a>
              </span>
            ))}
          </span>
        </p>
      </footer>
      <div data-lastmod-skip="end" hidden />
    </>
  );
}
